import { useCallback } from 'react';
import type { Reference } from '@apollo/client';
import type {
  Asset,
  CompleteAccountUserFragment,
  FolderAsset,
  Membership,
  Project,
  ResourceMembershipFragment,
  Role,
  UpdateMembershipPayload,
  Workspace,
} from '@frameio/graphql-client/generated';
import {
  CompleteAccountUser,
  ResourceMembership,
  useUpdateMembershipMutation,
} from '@frameio/graphql-client/generated';
import { intl } from '@frameio/i18n';
import { toSupportedLocaleOrDefault } from '@frameio/shared/utils/toSupportedLocale';
import { useToastTryCatchHandler } from '@frameio/utils/hooks/useToastTryCatchHandler';
import { useLocalOptimism } from '@frameio/vapor-migration/hooks';
import { useReadMembershipFragment } from '../utils';
import TEXT from './generated/TEXT/useUpdateMembership';

TEXT.DEFINE({
  /** Success toast message when member role is updated */
  success: (memberName: string) => `${memberName}'s role has been updated`,

  /** Error toast message when role update fails */
  error: (errorMessage?: string) =>
    `Unable to update role${errorMessage ? `: ${errorMessage}` : ''}`,
});

type UpdateMembershipProps = {
  availableRoles: Role[];
  membershipId: Membership['id'];
  roleId?: Role['id'];
  roleName?: Role['name'];
};

export type UpdateMembershipMutationArgs = {
  resourceId?:
    | Asset['id']
    | FolderAsset['id']
    | Project['id']
    | Workspace['id'];
};

export type UpdateMembershipArgs = {
  apolloMutationOptions?: Parameters<typeof useUpdateMembershipMutation>[0];
  roleId: NonNullable<Membership['role']>['id'];
};

export type UpdateMembershipType = (
  args: UpdateMembershipArgs
) => Promise<UpdateMembershipPayload>;

/**
 * This hook provides an interface to update a membership's role and the most
 * recent state of that role from the mutation response
 * @param member The Membership to reference
 * @returns A function to update its role as well as the current role id and
 * role name
 */
export const useUpdateMembership = ({
  availableRoles,
  membershipId,
  roleId,
  roleName,
}: UpdateMembershipProps): [
  UpdateMembershipType,
  { currentRoleId: Role['id']; currentRoleName: Role['name'] },
] => {
  const tryCatchOnSubmit = useToastTryCatchHandler();
  const locale = toSupportedLocaleOrDefault(intl().locale);
  const [updateMembershipMutation] = useUpdateMembershipMutation();
  const { member } = useReadMembershipFragment(membershipId);

  const [optimisticRoleId, setOptimisticRoleId] = useLocalOptimism(
    roleId || member?.role?.id || ''
  );
  const [optimisticRoleName, setOptimisticRoleName] = useLocalOptimism(
    roleName || member?.role?.name || ''
  );

  const updateMembership = useCallback(
    async ({
      apolloMutationOptions,
      roleId: newRoleId,
    }: UpdateMembershipArgs) => {
      setOptimisticRoleId(newRoleId);
      setOptimisticRoleName(
        availableRoles.find((role) => role.id === newRoleId)?.name ?? ''
      );

      const response = await new Promise((resolve) => {
        const update = updateMembershipMutation({
          variables: {
            locale,
            input: {
              id: member.id,
              roleId: newRoleId,
            },
          },
          update: (cache, { data }) => {
            const updatedMembership = data?.updateMembership?.membership;
            if (!updatedMembership) return;

            // Always write updated role data; if the membership id changed,
            const writeFragmentData =
              member.__typename === 'Membership'
                ? {
                    fragment: ResourceMembership,
                    fragmentName: 'ResourceMembership',
                    data: {
                      ...member,
                      id: updatedMembership.id ?? member.id,
                      role: updatedMembership.role,
                    },
                  }
                : {
                    fragment: CompleteAccountUser,
                    fragmentName: 'CompleteAccountUser',
                    data: {
                      ...member,
                      id: updatedMembership.id ?? member.id,
                    },
                  };

            const updatedMembershipRef = cache.writeFragment<
              ResourceMembershipFragment | CompleteAccountUserFragment
            >({
              id: cache.identify({
                __typename: member.__typename,
                id: updatedMembership.id ?? member.id,
              }),
              ...writeFragmentData,
            });

            // Membership IDs are role-derived and change after a role update.
            // Replace the stale ref in connection lists
            // When you change abc's role from "Full Access" to "Editor", the backend returns a new ID:
            // Apollo creates a new cache entry for that, but the list still points to the old one:
            // directMemberships.nodes
            //   → Membership:workspace/.../full_access/user/abc   ← stale, doesn't exist anymore
            //   → Membership:workspace/.../viewer/user/def
            //
            // The replaceRef replaces the old pointer with the new one:
            // directMemberships.nodes
            //   → Membership:workspace/.../editor/user/abc   ← updated
            //   → Membership:workspace/.../viewer/user/def
            const resourceCacheId =
              updatedMembership.resource &&
              cache.identify(updatedMembership.resource);
            if (
              member.id !== updatedMembership.id &&
              updatedMembershipRef &&
              resourceCacheId
            ) {
              const ref = updatedMembershipRef as Reference;
              cache.modify({
                id: resourceCacheId,
                fields: {
                  directMemberships: (existing) =>
                    replaceRef(existing, member.id, ref, cache),
                  indirectMemberships: (existing) =>
                    replaceRef(existing, member.id, ref, cache),
                },
              });
            }
          },
          ...apolloMutationOptions,
        });

        tryCatchOnSubmit({
          attempt: () => update,
          successMessage(success) {
            const membership = success.data.updateMembership.membership;
            resolve(membership);

            return TEXT.success(
              membership.accountUser?.name ||
                membership.accountUser?.inviteEmailAddress ||
                membership.accountUserGroup?.name
            ) as string;
          },
          errorMessage(error) {
            resolve(error);

            return TEXT.error(error) as string;
          },
        });

        return update;
      });

      return response as Promise<UpdateMembershipPayload>;
    },
    [
      availableRoles,
      locale,
      member,
      setOptimisticRoleId,
      setOptimisticRoleName,
      tryCatchOnSubmit,
      updateMembershipMutation,
    ]
  );

  return [
    updateMembership,
    {
      currentRoleId: optimisticRoleId,
      currentRoleName: optimisticRoleName,
    },
  ];
};

function replaceRef(
  connection: { nodes?: Reference[] },
  oldId: string,
  newRef: Reference,
  cache: { identify: (obj: any) => string | undefined }
) {
  if (!connection?.nodes?.length) return connection;
  const oldCacheId = cache.identify({ __typename: 'Membership', id: oldId });
  return {
    ...connection,
    nodes: connection.nodes.map((nodeRef) =>
      nodeRef.__ref === oldCacheId ? newRef : nodeRef
    ),
  };
}
